import React, { useEffect, useState } from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiDownload, FiFileText, FiX } from "react-icons/fi";
import { clientInvoiceService } from "../../../services/client/clientInvoiceService";
import ClientInvoiceStatusBadge from "../invoices/ClientInvoiceStatusBadge";

function dateLabel(value) {
  if (!value) return "Not provided";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
}

function money(value, currency) {
  if (value === null || value === undefined || value === "") return "Not available";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Not available";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount / 100);
  } catch {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(amount / 100);
  }
}

function Detail({ label, value }) {
  return (
    <div className="min-w-0 rounded-xl bg-slate-50 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-semibold text-slate-800">
        {value || "Not provided"}
      </p>
    </div>
  );
}

function lineQuantity(item) {
  return item.quantity ?? item.qty ?? null;
}

function lineUnitAmount(item) {
  return item.unit_amount ?? item.price ?? null;
}

export default function ClientInvoiceDetailsModal({
  organizationId,
  invoice: selectedInvoice,
  currency = "USD",
  onClose,
}) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestKey = selectedInvoice
    ? `${organizationId}:${selectedInvoice.id}`
    : "";

  useEffect(() => {
    if (!selectedInvoice || !organizationId) {
      setInvoice(null);
      return undefined;
    }

    let current = true;
    setInvoice(null);
    setLoading(true);
    setError("");

    clientInvoiceService
      .getClientInvoiceDetails({
        organizationId,
        invoiceId: selectedInvoice.id,
      })
      .then((details) => {
        if (current) setInvoice(details);
      })
      .catch((loadError) => {
        if (current) {
          setError(loadError?.message || "Unable to load invoice details.");
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [organizationId, requestKey]);

  useEffect(() => {
    if (!selectedInvoice) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedInvoice, onClose]);

  if (!selectedInvoice) return null;

  const displayed = invoice || selectedInvoice;
  const invoiceCurrency = displayed.currency || currency;
  const items = displayed.items || [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-invoice-title"
        className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-3xl sm:rounded-3xl"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-5 py-4 sm:px-7">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-violet-700">
              Read-only invoice
            </p>
            <h2 id="client-invoice-title" className="mt-1 break-words text-xl font-black text-slate-900">
              {displayed.invoice_number || "Invoice details"}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {displayed.invoice_pdf_url && (
              <a
                href={displayed.invoice_pdf_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50"
              >
                <SafeIcon icon={FiDownload} />
                PDF
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close invoice details"
              className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            >
              <SafeIcon icon={FiX} className="text-lg" />
            </button>
          </div>
        </header>

        <div className="space-y-5 p-5 sm:p-7">
          {loading && (
            <p className="text-sm font-medium text-slate-500">Loading invoice details…</p>
          )}
          {error && (
            <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <ClientInvoiceStatusBadge status={displayed.displayStatus || displayed.status} />
            <p className="text-xs text-slate-500">
              {displayed.payment_terms || displayed.terms
                ? `Terms: ${displayed.payment_terms || displayed.terms}`
                : "Payment terms not provided"}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Detail label="Customer" value={displayed.customer?.name || displayed.customer?.company_name} />
            <Detail label="Email" value={displayed.customer?.email} />
            <Detail label="Phone" value={displayed.customer?.phone} />
            <Detail label="Issue date" value={dateLabel(displayed.issue_date)} />
            <Detail label="Due date" value={dateLabel(displayed.due_date)} />
            <Detail
              label="Billing address"
              value={[
                displayed.customer?.address_line1,
                displayed.customer?.address_line2,
                displayed.customer?.city,
                displayed.customer?.state,
                displayed.customer?.postal_code,
                displayed.customer?.country,
              ]
                .filter(Boolean)
                .join(", ")}
            />
          </div>

          <section className="overflow-hidden rounded-2xl border border-slate-200">
            <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
              <SafeIcon icon={FiFileText} className="text-violet-600" />
              <h3 className="text-sm font-bold text-slate-800">Line items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left text-sm">
                <thead className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-4 py-3">Product / service</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                    <th className="px-4 py-3 text-right">Unit price</th>
                    <th className="px-4 py-3 text-right">Discount</th>
                    <th className="px-4 py-3 text-right">Tax</th>
                    <th className="px-4 py-3 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-800">
                          {item.product || item.description || "Item"}
                        </p>
                        {item.product && item.description && (
                          <p className="mt-1 text-xs text-slate-500">{item.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{lineQuantity(item) ?? "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {money(lineUnitAmount(item), invoiceCurrency)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {item.discount == null ? "—" : money(item.discount, invoiceCurrency)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {item.tax_rate == null ? "—" : `${item.tax_rate}%`}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">
                        {money(item.amount, invoiceCurrency)}
                      </td>
                    </tr>
                  ))}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-sm text-slate-500">
                        No line items are available for this invoice.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="ml-auto w-full max-w-sm space-y-2 rounded-2xl bg-slate-50 p-4 text-sm">
            <div className="flex justify-between gap-4 text-slate-600">
              <span>Subtotal</span><span>{money(displayed.subtotal, invoiceCurrency)}</span>
            </div>
            <div className="flex justify-between gap-4 text-slate-600">
              <span>Discounts</span><span>{money(displayed.discount_amount ?? displayed.discount_total, invoiceCurrency)}</span>
            </div>
            <div className="flex justify-between gap-4 text-slate-600">
              <span>Taxes</span><span>{money(displayed.tax_amount ?? displayed.tax_total, invoiceCurrency)}</span>
            </div>
            <div className="flex justify-between gap-4 border-t border-slate-200 pt-2 font-bold text-slate-900">
              <span>Total</span><span>{money(displayed.total_amount, invoiceCurrency)}</span>
            </div>
            <div className="flex justify-between gap-4 text-slate-600">
              <span>Payments received</span><span>{money(displayed.amount_paid, invoiceCurrency)}</span>
            </div>
            <div className="flex justify-between gap-4 font-bold text-violet-800">
              <span>Remaining balance</span><span>{money(displayed.amount_due, invoiceCurrency)}</span>
            </div>
          </section>

          {displayed.memo && (
            <section className="rounded-xl border border-slate-200 p-4">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">Notes</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{displayed.memo}</p>
            </section>
          )}

          {displayed.paymentHistoryUnavailable ? (
            <p className="text-xs text-amber-700">
              Payment history could not be loaded. The invoice balance shown above is the stored invoice balance.
            </p>
          ) : displayed.payments?.length > 0 ? (
            <section className="rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm font-bold text-slate-800">Payment history</h3>
              <div className="mt-3 divide-y divide-slate-100">
                {displayed.payments.map((payment) => (
                  <div key={payment.id} className="flex flex-wrap justify-between gap-2 py-3 text-sm">
                    <div>
                      <p className="font-semibold text-slate-800">
                        {dateLabel(payment.payment_date)}
                      </p>
                      <p className="text-xs capitalize text-slate-500">
                        {payment.payment_method || "Payment"}
                        {payment.reference_number ? ` · ${payment.reference_number}` : ""}
                      </p>
                    </div>
                    <p className="font-bold text-slate-900">
                      {money(
                        payment.invoiceAllocations.reduce(
                          (total, allocation) => total + (Number(allocation.amount) || 0),
                          0,
                        ),
                        invoiceCurrency,
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ) : (
            <p className="text-xs text-slate-500">No payment allocations are recorded for this invoice.</p>
          )}
        </div>

        <footer className="border-t border-slate-100 px-5 py-4 text-right sm:px-7">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}